import React, { ReactElement, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import { getRoute } from 'constants/routes';
import { MATURED_PERSON_RECOVERY_URL_PATH } from 'constants/general';
import { UserMe } from 'services/user.service';
import ContextMenu, {
    ContextMenuOption,
} from 'components/contextMenu/ContextMenu';
import ConfirmModal from 'components/modal/ConfirmModal';
import { getTranslationKey, useTranslation } from 'utils/localization';
import { GlobalContext } from 'pages/_app';
import { MenuApiEnum } from 'components/interfaces/GeneralInterface';
import { TNamespaceEnum } from 'types/types';
import { getLocaleDateString } from 'utils/helpers/getLocalDateString';
import {
    removeTokenCookies,
    setTokenCookie,
} from 'utils/helpers/browser/cookies';
import { TokenPurpose } from 'utils/helpers/cookies.shared';
import { MessageType, redirectWithMessage } from 'components/layouts/Toast';
import AuthService from 'services/auth.service';
import styles from './UserMenu.module.scss';

interface Props {
    user: UserMe;
}

const UserMenu = ({ user }: Props): ReactElement => {
    const Router = useRouter();
    const { t } = useTranslation();
    const { menu } = useContext(GlobalContext);
    const [maturedConfirmOpen, setMaturedConfirmOpen] = useState(false);

    const allowPassChange = menu.includes(MenuApiEnum['user-set-password']);

    const options: ContextMenuOption[] = [
        ...(allowPassChange
            ? [
                  {
                      text: t('userMenu.passwordChange'),
                      onClick: () => {
                          Router.push(getRoute('/user/password'));
                      },
                  },
              ]
            : []),
        {
            text: t('userMenu.myProfile'),
            url: getRoute('/members/{0}', [user && user.person_id]),
        },
        {
            text: t('userMenu.logout'),
            onClick: () => {
                Router.push(getRoute('/logout'));
            },
        },
    ];

    const dateFormat = getLocaleDateString();

    const personSwitch =
        user.user_persons.length == 1
            ? [] //If there is just single person, we don't want to display any options apart from 'Sign out'.
            : user.user_persons.map(
                  ({
                      person_id,
                      name,
                      date_of_birth,
                      gender,
                      has_matured,
                      is_active,
                  }): ContextMenuOption => ({
                      text: t(
                          getTranslationKey(
                              TNamespaceEnum.common,
                              'user.personToSwitch'
                          ),
                          {
                              name: name,
                              birthDate: format(
                                  new Date(date_of_birth),
                                  dateFormat
                              ),
                              suffix: t(
                                  getTranslationKey(
                                      TNamespaceEnum.common,
                                      user.person_id === person_id
                                          ? gender === 'male'
                                              ? 'user.signedInMale'
                                              : 'user.signedInFemale'
                                          : has_matured
                                          ? gender === 'male'
                                              ? 'user.matureMale'
                                              : 'user.matureFemale'
                                          : 'user.noSuffix'
                                  )
                              ),
                          }
                      ),
                      // We don't want the currently selected user having an active link:
                      isDisabled:
                          user.person_id === person_id ||
                          has_matured ||
                          !is_active,
                      title: has_matured
                          ? (t(
                                getTranslationKey(
                                    TNamespaceEnum.common,
                                    'user.maturedTooltip'
                                )
                            ) as string)
                          : !is_active
                          ? (t(
                                getTranslationKey(
                                    TNamespaceEnum.common,
                                    'user.inactiveTooltip'
                                )
                            ) as string)
                          : undefined,
                      // A matured minor opens the account migration modal, an
                      // inactive person has no handler at all - the API would
                      // reject the switch with `person_not_active` anyway:
                      onClick: has_matured
                          ? () => setMaturedConfirmOpen(true)
                          : is_active
                          ? async () => {
                                new AuthService()
                                    .switchUserPerson({ person_id })
                                    .then(async (res) => {
                                        // Error when switching person
                                        if (!res.access_token) return;

                                        // Keep refresh cookie same, only selected person has been changed
                                        setTokenCookie(
                                            res.access_token,
                                            TokenPurpose.Access
                                        );

                                        // Redirect to home
                                        redirectWithMessage('/', {
                                            messageType: MessageType.SUCCESS,
                                            message:
                                                'user_switch_success_message',
                                        });
                                    });
                            }
                          : undefined,
                  })
              );

    options.push(...personSwitch);

    // TODO IT-332: check if registration is possible
    options.push({
        text: t('userMenu.registerMinor'),
        onClick: () => {
            Router.push(getRoute('/signup'));
        },
    });
    options.push({
        text: t('userMenu.personRecovery'),
        onClick: () => {
            Router.push(getRoute('/person-recovery'));
        },
    });

    return (
        <>
            <ContextMenu options={options}>
                <div className={styles.userMenu}>
                    <span className={styles.userName}>{user && user.name}</span>
                    <span className={styles.icon} />
                </div>
            </ContextMenu>
            {maturedConfirmOpen && (
                <ConfirmModal
                    show
                    onHide={() => setMaturedConfirmOpen(false)}
                    onConfirm={async () => {
                        // Clear auth cookies BEFORE navigating so /person-recovery
                        // does not see a logged-in user and link the recovery to
                        // the current account (`linked: true` in the API payload).
                        // Direct removeTokenCookies() instead of logout() because
                        // logout() would redirect to IS1 logout URL and the
                        // /person-recovery navigation would never happen.
                        removeTokenCookies();
                        setMaturedConfirmOpen(false);
                        // Use Next router (Button wraps onClick in debounce + setTimeout
                        // which can lose Safari's user-gesture context for window.location).
                        await Router.push(MATURED_PERSON_RECOVERY_URL_PATH);
                    }}
                    title={
                        t(
                            getTranslationKey(
                                TNamespaceEnum.common,
                                'user.maturedConfirmTitle'
                            )
                        ) as string
                    }
                    text={
                        t(
                            getTranslationKey(
                                TNamespaceEnum.common,
                                'user.maturedConfirm'
                            )
                        ) as string
                    }
                    confirmButtonText={
                        t(
                            getTranslationKey(
                                TNamespaceEnum.common,
                                'user.maturedConfirmAction'
                            )
                        ) as string
                    }
                    textToLeft
                />
            )}
        </>
    );
};

export default UserMenu;
